import { EMPTY_CATEGORY_KEYS } from '@/storybook/components/FormCollection/utils/categoryOptions';
import { useCallback, useMemo, useState } from 'react';

type UseCollapsibleCategoriesParams = {
  isEnabled: boolean;
  isDefaultCollapsed: boolean;
  isSearching: boolean;
};

export type UseCollapsibleCategoriesReturn = {
  isCollapsibleActive: boolean;
  isCategoryCollapsed?: (categoryKey: string) => boolean;
  toggleCategory: (categoryKey: string) => void;
  resetCategories: VoidFunction;
};

export function useCollapsibleCategories({
  isEnabled,
  isDefaultCollapsed,
  isSearching,
}: UseCollapsibleCategoriesParams): UseCollapsibleCategoriesReturn {
  const [toggledCategoryKeys, setToggledCategoryKeys] = useState<ReadonlySet<string>>(EMPTY_CATEGORY_KEYS);

  const isCollapsibleActive = isEnabled && !isSearching;

  const isCategoryCollapsed = useMemo(() => {
    if (!isCollapsibleActive) return undefined;
    return (categoryKey: string) => toggledCategoryKeys.has(categoryKey) !== isDefaultCollapsed;
  }, [isCollapsibleActive, toggledCategoryKeys, isDefaultCollapsed]);

  const toggleCategory = useCallback((categoryKey: string) => {
    setToggledCategoryKeys(previousKeys => {
      const nextKeys = new Set(previousKeys);
      if (nextKeys.has(categoryKey)) {
        nextKeys.delete(categoryKey);
      } else {
        nextKeys.add(categoryKey);
      }
      return nextKeys;
    });
  }, []);

  const resetCategories = useCallback(() => {
    setToggledCategoryKeys(EMPTY_CATEGORY_KEYS);
  }, []);

  return { isCollapsibleActive, isCategoryCollapsed, toggleCategory, resetCategories };
}
